import React, { useState } from 'react';
import { useLoaderData, Form, redirect, useNavigation } from 'react-router-dom';
import classNames from 'classnames';
import { startCase, lowerCase } from 'lodash-es';
import {
  VaRadio,
  VaRadioOption,
  VaLoadingIndicator,
  VaBreadcrumbs,
  VaAlert,
  VaLink,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  formatStatus,
  BANNER_TYPES,
  DETAILS_BC_LABEL,
  PROCESSING_BANNER,
  ERROR_BANNER,
  poaDetailsBreadcrumbs,
  hideStatus,
} from '../utilities/poaRequests';
import { recordDatalayerEvent } from '../utilities/analytics';
import api from '../utilities/api';
import ProcessingBanner from '../components/ProcessingBanner';
import POADetailsColumn from '../components/POADetailsColumn';
import POADetailsAuthorization from '../components/POADetailsAuthorization';
import KeyValueTable from '../components/KeyValueTable';

const DECISION_TYPES = {
  ACCEPTANCE: 'acceptance',
  DECLINATION: 'declination',
};

const DECLINATION_OPTIONS = {
  DECLINATION_LIMITED_AUTH: {
    type: DECISION_TYPES.DECLINATION,
    declinationReason: 'Decline, because authorization is limited',
  },
  DECLINATION_OUTSIDE_SERVICE_TERRITORY: {
    type: DECISION_TYPES.DECLINATION,
    declinationReason:
      'Decline, because the claimant is outside of the organization’s service territory',
  },
  DECLINATION_OTHER: {
    type: DECISION_TYPES.DECLINATION,
    declinationReason: 'Decline, because of another reason',
  },
};

const DECISION_OPTIONS = {
  ACCEPTANCE: {
    type: DECISION_TYPES.ACCEPTANCE,
    declinationReason: null,
  },
  ...DECLINATION_OPTIONS,
};

const POARequestDetailsPage = () => {
  const loaderData = useLoaderData();
  const [error, setError] = useState();
  const [decisionValue, setDecisionValue] = useState();
  const navigation = useNavigation();

  const poaRequest = loaderData?.error403 ? null : loaderData;
  const poaRequestSubmission =
    poaRequest?.powerOfAttorneyFormSubmission?.status;

  const relationship =
    startCase(
      lowerCase(poaRequest?.powerOfAttorneyForm.claimant.relationship),
    ) || 'Self';

  const claimantIsUnestablishedDependent =
    relationship !== 'Self' && !poaRequest?.dependentRelationshipEstablished;

  if (loaderData?.error403) {
    return (
      <>
        <VaBreadcrumbs
          breadcrumbList={poaDetailsBreadcrumbs}
          label={DETAILS_BC_LABEL}
          homeVeteransAffairs={false}
          wrapping
        />
        <va-alert status="warning" data-testid="poa-request-details-403">
          <h2 slot="headline">You can’t review this representation request</h2>
          <p>
            Your Veterans Service Organization only allows you to review details
            of requests where you are the preferred representative. You are not
            the preferred representative on this request.
          </p>
        </va-alert>
      </>
    );
  }

  const handleChange = e => {
    e.preventDefault();
    const radioValue = e.detail?.value;
    if (radioValue) {
      setError();
    } else {
      setError(true);
    }

    setDecisionValue(radioValue);
  };

  const poaStatus =
    poaRequest.resolution?.decisionType ||
    poaRequest.resolution?.type ||
    'Pending';
  const city = poaRequest?.powerOfAttorneyForm.claimant.address.city;
  const state = poaRequest?.powerOfAttorneyForm.claimant.address.stateCode;
  const zipCode = poaRequest?.powerOfAttorneyForm.claimant.address.zipCode;
  const phone = poaRequest?.powerOfAttorneyForm.claimant.phone;
  const formattedPhone = phone.replace(/[^a-zA-Z0-9]/g, '');
  const email = poaRequest?.powerOfAttorneyForm.claimant.email;
  const claimantFirstName = poaRequest?.powerOfAttorneyForm.claimant.name.first;
  const claimantLastName = poaRequest?.powerOfAttorneyForm.claimant.name.last;
  const { recordDisclosureLimitations } =
    poaRequest.powerOfAttorneyForm.authorizations;
  const addressChange =
    poaRequest?.powerOfAttorneyForm.authorizations.addressChange;

  document.title = `Representation request for ${claimantLastName}, ${claimantFirstName} | Accredited Representative Portal | Veterans Affairs`;

  const handleSubmit = e => {
    if (!decisionValue) {
      setError('Select an option to continue');
      e.preventDefault();
    } else if (
      claimantIsUnestablishedDependent &&
      decisionValue === 'ACCEPTANCE'
    ) {
      setError(
        'You can’t accept the request until the claimant is an established dependent',
      );
      e.preventDefault();
    }
    return true;
  };

  const notProvidedString = 'Not provided';

  const claimantSSNAndVAFileNumberData = {
    'Last 4 digits of Social Security number':
      poaRequest?.powerOfAttorneyForm?.claimant?.ssn || notProvidedString,
    'Last 4 digits of VA file number':
      poaRequest?.powerOfAttorneyForm?.claimant?.vaFileNumber ||
      notProvidedString,
  };

  const veteranSSNAndVAFileNumberData = {
    'Last 4 digits of Social Security number':
      poaRequest?.powerOfAttorneyForm?.veteran?.ssn || notProvidedString,
    'Last 4 digits of VA file number':
      poaRequest?.powerOfAttorneyForm?.veteran?.vaFileNumber ||
      notProvidedString,
  };

  const claimantInfoData = {
    'Relationship to Veteran': relationship,
    Address: `${city}, ${state} ${zipCode}`,
    Phone: formattedPhone ? (
      <va-telephone contact={formattedPhone} not-clickable />
    ) : (
      notProvidedString
    ),
    Email: email,
    ...(relationship === 'Self' && { ...claimantSSNAndVAFileNumberData }),
  };
  const veteranInfoData = {
    Name: `${poaRequest?.powerOfAttorneyForm?.veteran?.name?.last}, ${poaRequest?.powerOfAttorneyForm?.veteran?.name?.first}`,
    ...veteranSSNAndVAFileNumberData,
  };

  setTimeout(() => {
    if (document.querySelector('va-radio')) {
      document
        .querySelector('va-radio')
        .shadowRoot?.querySelector('h2')
        .setAttribute('style', 'font-size:1.0625rem;');
      document
        .querySelector('va-radio')
        .shadowRoot?.querySelector('legend')
        .setAttribute('style', 'max-width:100%;');
    }
  }, '1000');

  return (
    <>
      <VaBreadcrumbs
        breadcrumbList={poaDetailsBreadcrumbs}
        label={DETAILS_BC_LABEL}
        homeVeteransAffairs={false}
        wrapping
      />
      {navigation.state === 'loading' ? (
        <VaLoadingIndicator message="Loading..." />
      ) : (
        <section className="poa-request-details">
          <h1
            className="poa-request-details__header vads-u-display--flex vads-u-flex-wrap--wrap vads-u-align-items--center"
            data-testid="poa-request-details-header"
            tabIndex={-1}
            aria-labelledby={`poa-request-details-heading-h1 poa-request-details-heading-name ${
              hideStatus(poaRequestSubmission)
                ? ``
                : `poa-request-details-heading-status`
            }`}
          >
            <span
              id="poa-request-details-heading-h1"
              className="poa-request-details__heading"
            >
              Representation request
            </span>
            <span
              id="poa-request-details-heading-name"
              className="poa-request-details__name"
            >
              {claimantLastName}, {claimantFirstName}
            </span>
            {poaStatus !== 'expired' && !hideStatus(poaRequestSubmission) && (
              <span
                id="poa-request-details-heading-status"
                className="usa-label vads-u-font-family--sans vads-u-font-weight--normal poa-request-details__status status status--processing"
              >
                {poaRequestSubmission === BANNER_TYPES.PROCESSING
                  ? 'processing'
                  : formatStatus(poaStatus)}
              </span>
            )}
          </h1>

          <POADetailsColumn poaRequest={poaRequest} poaStatus={poaStatus} />

          <div className="poa-request-details__info vads-u-margin-top--2">
            {poaRequestSubmission === BANNER_TYPES.PROCESSING && (
              <ProcessingBanner
                status="info"
                representative={
                  poaRequest?.resolution?.accreditedIndividual.fullName ||
                  'Your organization'
                }
                header={PROCESSING_BANNER.HEADER}
                accepted={PROCESSING_BANNER.ACCEPTED}
                date={poaRequest.resolution?.createdAt}
                copy={PROCESSING_BANNER.COPY}
              />
            )}

            {poaRequestSubmission === BANNER_TYPES.FAILED && (
              <ProcessingBanner
                status="error"
                header={ERROR_BANNER.HEADER}
                copy={ERROR_BANNER.COPY}
              />
            )}
            <VaAlert
              status="warning"
              visible={claimantIsUnestablishedDependent}
              data-testid="unestablished-dependent-warning1"
            >
              <h2>
                You can’t accept the request until the claimant is an
                established dependent
              </h2>
              The claimant who is requesting representation is not currently
              established as a dependent in VA records. Contact the claimant to
              help them become an established dependent using VA Form 21-686c.
              <p>
                <VaLink
                  href="/forms/21-686c/"
                  text="Go to VA Form 21-686c on VA.gov"
                  external
                />
              </p>
              Once the claimant is an established dependent, you can refresh
              this page and accept the request.
            </VaAlert>
            <h2
              id="claimant-information-heading"
              className="poa-request-details__h2 vads-u-margin-top--4"
              data-testid="claimant-information-heading"
            >
              Claimant information
            </h2>
            <KeyValueTable
              headingId="claimant-information-heading"
              data={claimantInfoData}
              dataTestId="claimant-information-table"
            />

            {/* This section will only appear if the claimant is a non-Veteran */}
            {poaRequest.powerOfAttorneyForm.veteran && (
              <>
                <h2
                  id="veteran-identification-information-heading"
                  className="poa-request-details__h2"
                  data-testid="veteran-identification-information-heading"
                >
                  Veteran identification information
                </h2>
                <KeyValueTable
                  headingId="veteran-identification-information-heading"
                  data={veteranInfoData}
                  dataTestId="veteran-identification-information-table"
                />
              </>
            )}

            {/* Authorization component */}
            <POADetailsAuthorization
              recordDisclosureLimitations={recordDisclosureLimitations}
              addressChange={addressChange}
            />

            <VaAlert
              status="warning"
              visible={claimantIsUnestablishedDependent}
              className="claimant-unestablished-warning"
              data-testid="unestablished-dependent-warning2"
            >
              <p className="vads-u-margin--0">
                You can’t accept the request until the claimant is an
                established dependent.
              </p>
            </VaAlert>

            {poaStatus === 'Pending' && (
              <Form
                method="post"
                action="decision"
                onSubmit={handleSubmit}
                className={classNames({
                  'poa-request-details__form': true,
                  'poa-request-details__form--error': error,
                  'poa-request-details__form--claimant-unestablished-warning':
                    claimantIsUnestablishedDependent,
                })}
              >
                <VaRadio
                  header-aria-describedby={null}
                  label="Do you accept or decline this representation request?"
                  label-header-level={2}
                  class="poa-request-details__form-label"
                  onVaValueChange={handleChange}
                  required
                  error={error}
                  onRadioOptionSelected={recordDatalayerEvent}
                  enable-analytics="false"
                >
                  <p>
                    <strong>Note:</strong> If you’d like the claimant to make
                    changes to their request, ask them to resubmit online VA
                    Form 21-22 with those changes. Their new request will
                    replace this one in the portal.
                  </p>
                  <p>
                    If you accept or decline, we’ll send the claimant an email
                    letting them know your decision.
                  </p>
                  <VaRadioOption
                    label="Accept"
                    value="ACCEPTANCE"
                    name="decision"
                    data-eventname="int-radio-button-option-click"
                  />

                  {Object.entries(DECLINATION_OPTIONS).map(
                    ([value, decision]) => (
                      <VaRadioOption
                        key={value}
                        label={decision.declinationReason}
                        value={value}
                        name="decision"
                        data-eventname="int-radio-button-option-click"
                      />
                    ),
                  )}
                </VaRadio>

                {/* eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component */}
                <button
                  type="submit"
                  className="usa-button poa-request-details__form-submit"
                >
                  Submit response
                </button>
              </Form>
            )}
          </div>
        </section>
      )}
    </>
  );
};

POARequestDetailsPage.loader = async ({ params, request }) => {
  try {
    return await api.getPOARequest(params.id, {
      signal: request.signal,
      skip403Redirect: true,
    });
  } catch (err) {
    if (err instanceof Response && err.status === 403) {
      try {
        await api.checkAuthorized({
          signal: request.signal,
          skip403Redirect: true,
        });

        return { error403: true };
      } catch {
        throw err;
      }
    }

    throw err;
  }
};

POARequestDetailsPage.createDecisionAction = async ({ params, request }) => {
  const key = (await request.formData()).get('decision');
  const decision = {
    ...DECISION_OPTIONS[key], // Spread the existing decision object
    key, // Add the key field with the value of the key
  };

  await api.createPOARequestDecision(params.id, decision, {
    signal: request.signal,
  });

  return redirect('..');
};

export default POARequestDetailsPage;
